import { lazy, Suspense, ReactNode } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ProtectedRoute from '../components/ProtectedRoute';
import PermissionProtectedRoute from '../components/PermissionProtectedRoute';
import { VerificationGuard } from '../components/auth/VerificationGuard';
import PageTitle from '../components/PageTitle';

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const JobsPage = lazy(() => import('../pages/JobsPage'));
const JobDetailPage = lazy(() => import('../pages/JobDetailPage'));
const JobFormPage = lazy(() => import('../pages/JobFormPage'));
const AnalyticsPage = lazy(() => import('../pages/AnalyticsPage'));
const FinancialDashboardPage = lazy(() => import('../pages/FinancialDashboardPage'));
const MessagesPage = lazy(() => import('../pages/MessagesPage'));
const CalendarPage = lazy(() => import('../pages/CalendarPage'));
const StaffSchedulePage = lazy(() => import('../pages/StaffSchedulePage'));
const LeadPipelinePage = lazy(() => import('../pages/LeadPipelinePage'));
const ClientJobPipelinePage = lazy(() => import('../pages/ClientJobPipelinePage'));
const TradeMyReviewsPage = lazy(() => import('../pages/TradeMyReviewsPage'));
const WarrantyClaimsPage = lazy(() => import('../pages/WarrantyClaimsPage'));
const PendingFollowUpsPage = lazy(() => import('../pages/PendingFollowUpsPage'));
const WorkflowApprovalPage = lazy(() => import('../pages/WorkflowApprovalPage'));
const QuotesPage = lazy(() => import('../pages/QuotesPage'));
const ServiceAreaMapPage = lazy(() => import('../pages/ServiceAreaMapPage'));
const QuoteTemplatesPage = lazy(() => import('../pages/QuoteTemplatesPage'));
const StaffPerformancePage = lazy(() => import('../pages/StaffPerformancePage'));


const TradeDirectoryPage = lazy(() => import('../pages/TradeDirectoryPage'));
const TradePaymentDashboard = lazy(() => import('../components/payments/TradePaymentDashboard'));
const QRManagementPage = lazy(() => import('../pages/qr-management'));
const QrStudioPage = lazy(() => import('../pages/qr-management/homeowner-qr/studio/QrStudioPage'));
const ClientReceivedReviewsPage = lazy(() => import('../pages/ClientReceivedReviewsPage'));

// Direct-to-Customer pages
const DirectCustomersPage = lazy(() => import('../pages/direct/DirectCustomersPage'));
const DirectQuotesPage = lazy(() => import('../pages/direct/DirectQuotesPage'));
const DirectJobsPage = lazy(() => import('../pages/direct/DirectJobsPage'));
const BrandingPage = lazy(() => import('../pages/direct/BrandingPage'));
const DirectQuoteCreatePage = lazy(() => import('../pages/direct/DirectQuoteCreatePage'));
const DirectQuoteDetailPage = lazy(() => import('../pages/direct/DirectQuoteDetailPage'));

// Manufacturer Fleet pages
const FleetListPage = lazy(() => import('../pages/fleet/FleetListPage'));
const FleetDetailPage = lazy(() => import('../pages/fleet/FleetDetailPage'));
const FleetImportPage = lazy(() => import('../pages/fleet/FleetImportPage'));
const FleetCasesPage = lazy(() => import('../pages/fleet/cases/FleetCasesPage'));
const FleetCaseDetailPage = lazy(() => import('../pages/fleet/cases/FleetCaseDetailPage'));
const FleetApprovalsPage = lazy(() => import('../pages/fleet/approvals/FleetApprovalsPage'));
const FleetCataloguePage = lazy(() => import('../pages/fleet/catalogue/FleetCataloguePage'));
const FleetNetworkPage = lazy(() => import('../pages/fleet/network/FleetNetworkPage'));

function RouteFallback() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
        </Box>
    );
}

function suspense(element: ReactNode) {
    return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

function BillingRedirect() {
    const location = useLocation();
    return <Navigate to={{ pathname: '/dashboard/financials', search: location.search }} replace />;
}

export default function DashboardRoutes() {
    return (
        <Routes>
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Dashboard">{suspense(<DashboardPage />)}</PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/jobs"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Jobs">{suspense(<JobsPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/jobs/:jobId"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Job Details">{suspense(<JobDetailPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/jobs/new"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:create']}>
                        <VerificationGuard>
                            <PageTitle title="Create Job">{suspense(<JobFormPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/jobs/:jobId/edit"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:edit']}>
                        <VerificationGuard>
                            <PageTitle title="Edit Job">{suspense(<JobFormPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/quotes"
                element={
                    <PermissionProtectedRoute requiredPermissions={['quote:view']}>
                        <VerificationGuard>
                            <PageTitle title="Quotes">{suspense(<QuotesPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/calendar"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Calendar">{suspense(<CalendarPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/staff-schedule"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:edit']}>
                        <VerificationGuard>
                            <PageTitle title="Staff Schedule">{suspense(<StaffSchedulePage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/lead-pipeline"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Job Pipeline">{suspense(<LeadPipelinePage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/delivery-pipeline"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Delivery Pipeline">{suspense(<ClientJobPipelinePage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/job-pipeline"
                element={<Navigate to="/dashboard/delivery-pipeline" replace />}
            />
            <Route
                path="/my-reviews"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="My Reviews">{suspense(<TradeMyReviewsPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/warranty"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Warranties">{suspense(<WarrantyClaimsPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/follow-ups"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Pending Follow-ups">{suspense(<PendingFollowUpsPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/workflow-approvals"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Workflow Approval">{suspense(<WorkflowApprovalPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/trades"
                element={
                    <PermissionProtectedRoute requiredPermissions={['user:view']}>
                        <VerificationGuard>
                            <PageTitle title="Trade Directory">{suspense(<TradeDirectoryPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/analytics"
                element={
                    <PermissionProtectedRoute requiredPermissions={['analytics:view']}>
                        <PageTitle title="Analytics">{suspense(<AnalyticsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/billing"
                element={<BillingRedirect />}
            />
            <Route
                path="/billing/terms-charges"
                element={<BillingRedirect />}
            />
            <Route
                path="/financials"
                element={
                    <PermissionProtectedRoute requiredPermissions={['financial:view', 'company:edit']}>
                        <VerificationGuard>
                            <PageTitle title="Financials">{suspense(<FinancialDashboardPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/payments"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Payments">{suspense(<TradePaymentDashboard />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/messages"
                element={
                    <ProtectedRoute>
                        <PageTitle title="Messages">{suspense(<MessagesPage />)}</PageTitle>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/messages/:conversationId"
                element={
                    <ProtectedRoute>
                        <PageTitle title="Messages">{suspense(<MessagesPage />)}</PageTitle>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/qr-codes"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:create', 'quote:create']} requireAllPermissions={false}>
                        <VerificationGuard>
                            <PageTitle title="QR Codes">{suspense(<QRManagementPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            {/* The QR studio for one homeowner QR code (part 3): the same guard as /qr-codes; the RPCs check the rest. */}
            <Route
                path="/qr-codes/studio/:codeId"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:create', 'quote:create']} requireAllPermissions={false}>
                        <VerificationGuard>
                            <PageTitle title="QR studio">{suspense(<QrStudioPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/reviews"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Reviews">{suspense(<ClientReceivedReviewsPage />)}</PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/service-area"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <VerificationGuard>
                            <PageTitle title="Service Area">{suspense(<ServiceAreaMapPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/quote-templates"
                element={
                    <PermissionProtectedRoute requiredPermissions={['quote:create']}>
                        <VerificationGuard>
                            <PageTitle title="Quote Templates">{suspense(<QuoteTemplatesPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/staff-performance"
                element={
                    <PermissionProtectedRoute requiredPermissions={['user:edit']}>
                        <VerificationGuard>
                            <PageTitle title="Staff Performance">{suspense(<StaffPerformancePage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            {/* Manufacturer Fleet routes */}
            <Route
                path="/fleet"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Fleet">{suspense(<FleetListPage />)}</PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/fleet/import"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Import Fleet Registrations">
                                {suspense(<FleetImportPage />)}
                            </PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/fleet/cases"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Fleet Cases">{suspense(<FleetCasesPage />)}</PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/fleet/cases/:id"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Service Case">{suspense(<FleetCaseDetailPage />)}</PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/fleet/approvals"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Fleet Approvals">{suspense(<FleetApprovalsPage />)}</PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/fleet/catalogue"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Service Catalogue">{suspense(<FleetCataloguePage />)}</PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/fleet/network"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Accredited Network">{suspense(<FleetNetworkPage />)}</PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/fleet/:id"
                element={
                    <ProtectedRoute>
                        <VerificationGuard>
                            <PageTitle title="Install Registration Detail">
                                {suspense(<FleetDetailPage />)}
                            </PageTitle>
                        </VerificationGuard>
                    </ProtectedRoute>
                }
            />
            {/* Direct-to-Customer routes */}
            <Route
                path="/direct/customers"
                element={
                    <PermissionProtectedRoute requiredPermissions={['quote:create']}>
                        <VerificationGuard>
                            <PageTitle title="Direct Customers">{suspense(<DirectCustomersPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/direct/quotes"
                element={
                    <PermissionProtectedRoute requiredPermissions={['quote:create']}>
                        <VerificationGuard>
                            <PageTitle title="Direct Quotes">{suspense(<DirectQuotesPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/direct/quotes/new"
                element={
                    <PermissionProtectedRoute requiredPermissions={['quote:create']}>
                        <VerificationGuard>
                            <PageTitle title="Create Quote">{suspense(<DirectQuoteCreatePage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/direct/quotes/:quoteId"
                element={
                    <PermissionProtectedRoute requiredPermissions={['quote:create']}>
                        <VerificationGuard>
                            <PageTitle title="Quote Details">{suspense(<DirectQuoteDetailPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/direct/jobs"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Direct Jobs">{suspense(<DirectJobsPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/direct/branding"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <VerificationGuard>
                            <PageTitle title="Branding">{suspense(<BrandingPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            {/* Fallback redirect for unmatched dashboard sub-routes */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}
